import { createElement, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ActionIntent, IconId } from '@finance/contracts';
import type { ResolvedAction } from '../../runtime/actions/action-resolution';
import {
  executeResolvedAction,
  type ActionExecutionContext,
} from '../../runtime/actions/action-executor';
import { useTelemetry } from '../../runtime/telemetry/use-telemetry';
import { resolveIcon } from '../../runtime/icons';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { action } from '../../styles/tokens/semantic.js';
import { useNotify } from '../../features/notifications/notification-store';

interface ActionIconProps {
  iconKey?: IconId | undefined;
}

function ActionIcon({ iconKey }: ActionIconProps) {
  if (!iconKey) return null;
  const IconComponent = resolveIcon(iconKey);
  if (!IconComponent) return null;
  return createElement(IconComponent, {
    fontSize: 'small',
    sx: { mr: 0.5 },
    'aria-hidden': 'true',
  });
}

export interface ActionRendererProps {
  action: ResolvedAction;
  onAction?: (action: ResolvedAction, context?: ActionExecutionContext) => void | Promise<void>;
  context?: ActionExecutionContext;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** Render as a semantic menuitem for an overflow action menu. */
  menuItem?: boolean;
}

export function ActionRenderer({
  action: resolved,
  onAction,
  context,
  size = 'medium',
  fullWidth = false,
  menuItem = false,
}: ActionRendererProps) {
  const { text } = useAppI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useTelemetry();
  const { notify } = useNotify();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationValues, setConfirmationValues] = useState<Record<string, unknown>>({});
  const [pending, setPending] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [inlineError, setInlineError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [isRetryable, setIsRetryable] = useState(false);
  const executingRef = useRef(false);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const descriptor = resolved.action;
  const label = text(descriptor.labelKey);
  const availability = descriptor.availability ?? 'AVAILABLE';
  const isHidden = availability === 'HIDDEN';
  const isReadOnly = availability === 'READ_ONLY';
  const canStepUp = availability === 'LOCKED_AAL' && Boolean(descriptor.successNavigateTo);
  const executableAvailability = availability === 'AVAILABLE' || availability === 'READ_ONLY';

  const closeConfirmation = useCallback(() => {
    setConfirmOpen(false);
    setConfirmationValues({});
    window.requestAnimationFrame(() => triggerButtonRef.current?.focus());
  }, []);

  const executeAction = useCallback(
    async (confirmationHandled = false, confirmedValues?: Record<string, unknown>) => {
      if (executingRef.current) return;
      if (!executableAvailability) return;
      if (resolved.disabled || !resolved.visible || !resolved.available) return;

      executingRef.current = true;
      setPending(true);
      setInlineError('');
      setAnnouncement('');
      try {
        const executionContext: ActionExecutionContext = context
          ? { ...context, confirmationHandled, confirmationValues: confirmedValues }
          : { source: 'page', confirmationHandled, confirmationValues: confirmedValues };
        await executeResolvedAction({ resolved, onAction, navigate, context: executionContext });
        if (descriptor.successBehavior === 'SHOW_TOAST' && descriptor.successMessageKey) {
          const successMessage = text(descriptor.successMessageKey);
          setAnnouncement(successMessage);
          notify(successMessage, 'success', 4000);
        }
      } catch (error) {
        const errorObject = error as {
          correlationId?: unknown;
          retryable?: unknown;
          name?: unknown;
        };
        track(descriptor.telemetryId, {
          eventName: 'action_execution_failed',
          actionId: descriptor.id,
          operationId: descriptor.operationId,
          route: location.pathname,
          correlationId: errorObject?.correlationId ? String(errorObject.correlationId) : undefined,
          errorClass: errorObject?.name ? String(errorObject.name) : 'UnknownError',
          retryable:
            typeof errorObject?.retryable === 'boolean' ? errorObject.retryable : undefined,
        });

        const errorRetryable =
          typeof errorObject?.retryable === 'boolean' ? errorObject.retryable : false;
        setIsRetryable(errorRetryable);

        const safeMsg = descriptor.errorMessageKey
          ? text(descriptor.errorMessageKey)
          : text('common.error.unexpected');
        setErrorMsg(safeMsg);
        setAnnouncement(safeMsg);

        if (descriptor.errorBehavior === 'SHOW_INLINE_ERROR') setInlineError(safeMsg);
        else if (descriptor.errorBehavior === 'SHOW_DIALOG_ERROR') setErrorDialogOpen(true);
        else notify(safeMsg, 'error', 7000);
      } finally {
        setPending(false);
        executingRef.current = false;
      }
    },
    [
      context,
      descriptor,
      executableAvailability,
      location.pathname,
      navigate,
      notify,
      onAction,
      resolved,
      text,
      track,
    ]
  );

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (canStepUp && descriptor.successNavigateTo) {
      navigate(descriptor.successNavigateTo);
      return;
    }
    if (!executableAvailability) return;
    if (resolved.disabled || !resolved.visible || !resolved.available) return;

    if (descriptor.confirmation) {
      setConfirmationValues({});
      setConfirmOpen(true);
      return;
    }
    void executeAction(false);
  };

  const isReadOnlyNonNavigate = isReadOnly && descriptor.kind !== 'navigate';
  const isDisabled =
    resolved.disabled ||
    pending ||
    !resolved.visible ||
    (!resolved.available && !canStepUp) ||
    (!executableAvailability && !canStepUp) ||
    isReadOnlyNonNavigate;

  if (isHidden || !resolved.visible) return null;

  const disabledReasonKey = resolved.disabledReasonKey ?? descriptor.disabledReasonKey;
  const disabledReason = disabledReasonKey ? text(disabledReasonKey) : undefined;

  const intentToToken: Record<ActionIntent, keyof typeof action> = {
    primary: 'primary',
    secondary: 'secondary',
    neutral: 'tertiary',
    warning: 'warning',
    danger: 'danger',
  };
  const resolvedIntent = descriptor.intent ?? 'primary';
  const { variant, color } = action[intentToToken[resolvedIntent]];
  const confirmationIsRisky = resolvedIntent === 'danger' || resolvedIntent === 'warning';
  const confirmationColor =
    resolvedIntent === 'danger'
      ? 'error'
      : resolvedIntent === 'warning'
        ? 'warning'
        : 'primary';
  const confirmationIncomplete = descriptor.confirmation?.fields?.some(field => {
    if (!field.required) return false;
    const value = confirmationValues[field.name];
    return field.fieldType === 'checkbox'
      ? value !== true
      : typeof value !== 'string' || value.trim().length === 0;
  });

  const trigger = menuItem ? (
    <MenuItem
      ref={triggerButtonRef}
      data-testid={`experience-action-${String(descriptor.id)}`}
      component="button"
      type="button"
      disabled={isDisabled}
      onClick={handleClick}
      aria-label={label}
      aria-busy={pending}
      sx={{ width: '100%', gap: 0.5 }}
    >
      {pending && <CircularProgress size={16} aria-hidden="true" />}
      {!pending && <ActionIcon iconKey={descriptor.iconKey as IconId | undefined} />}
      {label}
    </MenuItem>
  ) : (
    <Button
      ref={triggerButtonRef}
      data-testid={`experience-action-${String(descriptor.id)}`}
      type="button"
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      className={`action action-${descriptor.intent}`}
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={label}
      aria-busy={pending}
    >
      {pending && <CircularProgress size={16} sx={{ mr: 0.5 }} aria-hidden="true" />}
      {!pending && <ActionIcon iconKey={descriptor.iconKey as IconId | undefined} />}
      {label}
    </Button>
  );

  // Native disabled buttons are intentionally not focusable. Keep the reason
  // reachable to keyboard and assistive-technology users on their wrapper.
  const triggerWithReason = disabledReason ? (
    <Tooltip title={disabledReason} describeChild>
      <Box
        component="span"
        tabIndex={isDisabled ? 0 : undefined}
        aria-label={isDisabled ? `${label}: ${disabledReason}` : undefined}
        sx={{ display: 'inline-flex', width: fullWidth ? '100%' : 'auto' }}
      >
        {trigger}
      </Box>
    </Tooltip>
  ) : (
    trigger
  );

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          width: fullWidth ? '100%' : 'auto',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }}
      >
        {triggerWithReason}
        {inlineError && (
          <Alert
            severity="error"
            variant="outlined"
            sx={{ py: 0, maxWidth: 360 }}
            action={
              isRetryable ? (
                <Button color="inherit" size="small" onClick={() => void executeAction(false)}>
                  {text('common.retry')}
                </Button>
              ) : undefined
            }
          >
            {inlineError}
          </Alert>
        )}
      </Box>

      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {announcement}
      </Box>

      {descriptor.confirmation && (
        <Dialog
          open={confirmOpen}
          onClose={pending ? undefined : closeConfirmation}
          aria-labelledby={`action-confirm-${descriptor.id}-title`}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id={`action-confirm-${descriptor.id}-title`}>
            {text(descriptor.confirmation.titleKey)}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: descriptor.confirmation.fields?.length ? 2 : 0 }}>
              {text(descriptor.confirmation.messageKey)}
            </DialogContentText>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {descriptor.confirmation.fields?.map((field, index) =>
                field.fieldType === 'checkbox' ? (
                  <FormControlLabel
                    key={field.name}
                    control={
                      <Checkbox
                        checked={confirmationValues[field.name] === true}
                        onChange={event =>
                          setConfirmationValues(values => ({
                            ...values,
                            [field.name]: event.target.checked,
                          }))
                        }
                        disabled={pending}
                      />
                    }
                    label={text(field.labelKey)}
                  />
                ) : (
                  <TextField
                    key={field.name}
                    label={text(field.labelKey)}
                    value={
                      typeof confirmationValues[field.name] === 'string'
                        ? confirmationValues[field.name]
                        : ''
                    }
                    onChange={event =>
                      setConfirmationValues(values => ({
                        ...values,
                        [field.name]: event.target.value,
                      }))
                    }
                    required={field.required}
                    disabled={pending}
                    autoFocus={index === 0}
                    fullWidth
                    multiline
                    minRows={2}
                    slotProps={{ htmlInput: { 'data-testid': `confirmation-${field.name}` } }}
                  />
                )
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={closeConfirmation}
              color="inherit"
              autoFocus={confirmationIsRisky && !descriptor.confirmation.fields?.length}
              disabled={pending}
            >
              {descriptor.confirmation.cancelLabelKey
                ? text(descriptor.confirmation.cancelLabelKey)
                : text('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                void executeAction(true, confirmationValues).finally(() =>
                  setConfirmationValues({})
                );
              }}
              color={confirmationColor}
              variant="contained"
              autoFocus={!confirmationIsRisky && !descriptor.confirmation.fields?.length}
              disabled={pending || Boolean(confirmationIncomplete)}
            >
              {descriptor.confirmation.confirmLabelKey
                ? text(descriptor.confirmation.confirmLabelKey)
                : text('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          window.requestAnimationFrame(() => triggerButtonRef.current?.focus());
        }}
      >
        <DialogTitle>{text('common.states.error.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {isRetryable && (
            <Button
              onClick={() => {
                setErrorDialogOpen(false);
                void executeAction(false);
              }}
              variant="contained"
            >
              {text('common.retry')}
            </Button>
          )}
          <Button
            onClick={() => {
              setErrorDialogOpen(false);
              window.requestAnimationFrame(() => triggerButtonRef.current?.focus());
            }}
            variant={isRetryable ? 'outlined' : 'contained'}
          >
            {text('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
